import React, { useCallback, useEffect, useMemo, useState } from "react";
import { db, functions, httpsCallable } from "./firebase.js";
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs } from "firebase/firestore";
import { toast } from "react-toastify";
import { formatRating, ratingLabel, sortContractors } from "./contractorRatingUtils.js";
import { formatExternalLinksUpdatedAt, sortExternalRatingLinks } from "./contractorExternalRatings.js";

function ExternalRatingLinksCell({ contractor }) {
  const links = sortExternalRatingLinks(contractor.externalRatingLinks || []);
  const updatedLabel = formatExternalLinksUpdatedAt(contractor.externalRatingLinksUpdatedAt);

  if (!links.length) {
    return <span className="contractor-review-muted">Not found yet</span>;
  }

  return (
    <div className="contractor-external-links">
      {links.map((link) => (
        <a
          key={`${contractor.id}-${link.site}`}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="contractor-external-link"
        >
          {link.label}
        </a>
      ))}
      {updatedLabel ? <span className="contractor-external-updated">Updated {updatedLabel}</span> : null}
    </div>
  );
}

function StarDisplay({ value, size = "md" }) {
  const stars = Math.round(Number(value) || 0);
  return (
    <span className={`contractor-stars contractor-stars--${size}`} aria-label={`${value || 0} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= stars ? "contractor-star contractor-star--on" : "contractor-star"}>
          ★
        </span>
      ))}
    </span>
  );
}

function StarInput({ value, onChange, disabled }) {
  return (
    <div className="contractor-star-input" role="radiogroup" aria-label="Your rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={n <= value ? "contractor-star contractor-star--on contractor-star--btn" : "contractor-star contractor-star--btn"}
          onClick={() => onChange(n)}
          disabled={disabled}
          aria-label={`${n} star${n > 1 ? "s" : ""}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function ContractorForm({ onSubmit, onCancel, initialData = {}, isSubmitting, isMobile }) {
  const [formData, setFormData] = useState({
    businessName: initialData.businessName || "",
    contactName: initialData.contactName || "",
    address: initialData.address || "",
    phoneNumber: initialData.phoneNumber || "",
    emailAddress: initialData.emailAddress || "",
    webSite: initialData.webSite || "",
    notes: initialData.notes || "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="modal-overlay">
      <div className={`modal-content contractor-modal ${isMobile ? "modal-content-mobile" : ""}`}>
        <h2 className="modal-title">{initialData.businessName ? "Edit Contractor" : "Add Contractor"}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(formData);
          }}
        >
          <div className={`form-grid ${isMobile ? "form-grid-mobile" : ""}`}>
            <div className="form-group">
              <label className="form-label">Business Name</label>
              <input type="text" name="businessName" value={formData.businessName} onChange={handleChange} className="form-input" required />
            </div>
            <div className="form-group">
              <label className="form-label">Contact Name</label>
              <input type="text" name="contactName" value={formData.contactName} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Address</label>
              <textarea name="address" value={formData.address} onChange={handleChange} className="form-textarea" rows="3" required />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <input type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} className="form-input" required />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input type="email" name="emailAddress" value={formData.emailAddress} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group">
              <label className="form-label">Web Site</label>
              <input type="url" name="webSite" value={formData.webSite} onChange={handleChange} className="form-input" />
            </div>
            <div className="form-group full-width">
              <label className="form-label">Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} className="form-textarea" rows="3" required />
            </div>
          </div>
          <div className="modal-buttons">
            <button type="button" onClick={onCancel} className="cancel-button" disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="save-button" disabled={isSubmitting}>{isSubmitting ? "Saving…" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RatingPanel({ contractor, myRating, onClose, onSaved, isSubmitting, setIsSubmitting }) {
  const [stars, setStars] = useState(myRating?.stars || 0);
  const [comment, setComment] = useState(myRating?.comment || "");
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingReviews(true);
      try {
        const res = await httpsCallable(functions, "listContractorRatings")({ contractorId: contractor.id });
        if (active) setReviews(res.data?.ratings || []);
      } catch {
        if (active) setReviews([]);
      } finally {
        if (active) setLoadingReviews(false);
      }
    })();
    return () => { active = false; };
  }, [contractor.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stars) {
      toast.error("Select a star rating first.");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await httpsCallable(functions, "submitContractorRating")({
        contractorId: contractor.id,
        stars,
        comment,
      });
      toast.success("Rating saved — thank you!");
      onSaved({
        stars,
        comment,
        averageRating: res.data?.averageRating,
        ratingCount: res.data?.ratingCount,
      });
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save rating");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remove your rating for this contractor?")) return;
    setIsSubmitting(true);
    try {
      const res = await httpsCallable(functions, "deleteContractorRating")({ contractorId: contractor.id });
      toast.success("Rating removed");
      onSaved({
        stars: 0,
        comment: "",
        averageRating: res.data?.averageRating,
        ratingCount: res.data?.ratingCount,
        removed: true,
      });
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to remove rating");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content contractor-rating-modal">
        <div className="contractor-rating-modal-header">
          <div>
            <h2 className="modal-title">{contractor.businessName}</h2>
            <p className="contractor-rating-summary">
              <StarDisplay value={contractor.averageRating} />
              <span>{formatRating(contractor.averageRating)} · {contractor.ratingCount || 0} review{(contractor.ratingCount || 0) === 1 ? "" : "s"}</span>
            </p>
          </div>
          <button type="button" className="contractor-rating-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="contractor-rating-form" onSubmit={handleSubmit}>
          <p className="contractor-rating-form-label">Your rating</p>
          <StarInput value={stars} onChange={setStars} disabled={isSubmitting} />
          <textarea
            className="form-textarea"
            placeholder="Optional comment — quality, timeliness, communication…"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={3}
            maxLength={500}
            disabled={isSubmitting}
          />
          <div className="contractor-rating-form-actions">
            {myRating?.stars ? (
              <button type="button" className="delete-button" onClick={handleDelete} disabled={isSubmitting}>Remove my rating</button>
            ) : null}
            <button type="submit" className="save-button" disabled={isSubmitting || !stars}>{isSubmitting ? "Saving…" : "Submit rating"}</button>
          </div>
        </form>

        <section className="contractor-reviews-list">
          <h3>Owner reviews</h3>
          {loadingReviews ? <p>Loading reviews…</p> : null}
          {!loadingReviews && reviews.length === 0 ? <p className="contractor-reviews-empty">No reviews yet — be the first to rate this vendor.</p> : null}
          {reviews.map((review) => (
            <article key={review.id} className="contractor-review-card">
              <div className="contractor-review-top">
                <StarDisplay value={review.stars} size="sm" />
                <span className="contractor-review-date">
                  {review.updatedAt ? new Date(review.updatedAt).toLocaleDateString() : ""}
                </span>
              </div>
              {review.comment ? <p>{review.comment}</p> : <p className="contractor-review-muted">No written comment.</p>}
            </article>
          ))}
        </section>
      </div>
    </div>
  );
}

function ContractorsPage({ user, isAdmin, isMobile }) {
  const [contractors, setContractors] = useState([]);
  const [myRatings, setMyRatings] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [editContractor, setEditContractor] = useState(null);
  const [ratingContractor, setRatingContractor] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortBy, setSortBy] = useState("name");
  const [filterRated, setFilterRated] = useState("all");

  const loadContractors = useCallback(async () => {
    const snapshot = await getDocs(collection(db, "contractors"));
    return snapshot.docs.map((row) => ({ id: row.id, ...row.data() }));
  }, []);

  const loadMyRatings = useCallback(async () => {
    if (!user) return {};
    try {
      const res = await httpsCallable(functions, "getMyContractorRatings")({});
      return res.data?.ratings || {};
    } catch {
      return {};
    }
  }, [user]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [list, ratings] = await Promise.all([loadContractors(), loadMyRatings()]);
      setContractors(list);
      setMyRatings(ratings);
    } catch (err) {
      setError("Failed to load contractors: " + err.message);
    } finally {
      setIsLoading(false);
    }
  }, [loadContractors, loadMyRatings]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const displayedContractors = useMemo(() => {
    let rows = sortContractors(contractors, sortBy);
    if (filterRated === "mine") {
      rows = rows.filter((c) => myRatings[c.id]?.stars);
    } else if (filterRated === "top") {
      rows = rows.filter((c) => (Number(c.averageRating) || 0) >= 4);
    }
    return rows;
  }, [contractors, sortBy, filterRated, myRatings]);

  const handleAddContractor = async (newContractor) => {
    try {
      setError(null);
      setIsSubmitting(true);
      await addDoc(collection(db, "contractors"), { ...newContractor, averageRating: 0, ratingCount: 0 });
      setShowAddForm(false);
      await refresh();
      toast.success("Contractor added");
    } catch (err) {
      setError(err.code === "permission-denied" ? "Only admins can add contractors." : "Failed to add contractor: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditContractor = async (updatedContractor) => {
    try {
      setError(null);
      setIsSubmitting(true);
      await updateDoc(doc(db, "contractors", editContractor.id), updatedContractor);
      setEditContractor(null);
      await refresh();
      toast.success("Contractor updated");
    } catch (err) {
      setError(err.code === "permission-denied" ? "Only admins can edit contractors." : "Failed to edit contractor: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteContractor = async (contractorId) => {
    if (!window.confirm("Are you sure you want to delete this contractor?")) return;
    try {
      setError(null);
      setIsSubmitting(true);
      await deleteDoc(doc(db, "contractors", contractorId));
      await refresh();
      toast.success("Contractor deleted");
    } catch (err) {
      setError(err.code === "permission-denied" ? "Only admins can delete contractors." : "Failed to delete contractor: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRatingSaved = ({ averageRating, ratingCount, stars, comment, removed }) => {
    setContractors((prev) => prev.map((c) => (
      c.id === ratingContractor?.id
        ? { ...c, averageRating: averageRating ?? c.averageRating, ratingCount: ratingCount ?? c.ratingCount }
        : c
    )));
    if (ratingContractor) {
      setMyRatings((prev) => {
        const next = { ...prev };
        if (removed) delete next[ratingContractor.id];
        else next[ratingContractor.id] = { stars, comment };
        return next;
      });
    }
  };

  return (
    <div className={`contractors-page ${isMobile ? "contractors-page-mobile" : ""}`}>
      <header className="contractors-page-header">
        <div>
          <h1 className="page-title">Contractors</h1>
          <p className="contractors-page-lead">
            Board-approved vendors for the community. Owners can rate and review based on personal experience.
            Links to Google, Yelp, BBB, Angi, and Facebook profiles refresh nightly.
          </p>
        </div>
        {isAdmin ? (
          <button type="button" onClick={() => setShowAddForm(true)} className="add-button" disabled={isSubmitting}>
            Add contractor
          </button>
        ) : null}
      </header>

      {error ? <div className="error-message">{error}</div> : null}

      <div className="contractors-toolbar">
        <label className="contractors-toolbar-field">
          Sort
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Name (A–Z)</option>
            <option value="rating">Highest rated</option>
            <option value="reviews">Most reviews</option>
          </select>
        </label>
        <label className="contractors-toolbar-field">
          Filter
          <select value={filterRated} onChange={(e) => setFilterRated(e.target.value)}>
            <option value="all">All contractors</option>
            <option value="top">Rated 4+ stars</option>
            {user ? <option value="mine">Rated by me</option> : null}
          </select>
        </label>
      </div>

      {showAddForm ? (
        <ContractorForm onSubmit={handleAddContractor} onCancel={() => setShowAddForm(false)} isSubmitting={isSubmitting} isMobile={isMobile} />
      ) : null}
      {editContractor ? (
        <ContractorForm onSubmit={handleEditContractor} onCancel={() => setEditContractor(null)} initialData={editContractor} isSubmitting={isSubmitting} isMobile={isMobile} />
      ) : null}
      {ratingContractor ? (
        <RatingPanel
          contractor={ratingContractor}
          myRating={myRatings[ratingContractor.id]}
          onClose={() => setRatingContractor(null)}
          onSaved={handleRatingSaved}
          isSubmitting={isSubmitting}
          setIsSubmitting={setIsSubmitting}
        />
      ) : null}

      {isLoading ? (
        <p className="contractors-loading">Loading contractors…</p>
      ) : displayedContractors.length === 0 ? (
        <p className="contractors-empty">No contractors match this filter.</p>
      ) : isMobile ? (
        <div className="contractor-card-grid">
          {displayedContractors.map((contractor) => (
            <article key={contractor.id} className="contractor-card">
              <div className="contractor-card-top">
                <h2>{contractor.businessName}</h2>
                <div className="contractor-card-rating">
                  <StarDisplay value={contractor.averageRating} />
                  <span>{formatRating(contractor.averageRating)} ({contractor.ratingCount || 0})</span>
                </div>
                <p className="contractor-card-rating-label">{ratingLabel(contractor.averageRating)}</p>
              </div>
              <p><strong>Contact:</strong> {contractor.contactName || "—"}</p>
              <p><strong>Phone:</strong> {contractor.phoneNumber || "—"}</p>
              {contractor.emailAddress ? <p><strong>Email:</strong> {contractor.emailAddress}</p> : null}
              {contractor.webSite ? (
                <p><a href={contractor.webSite} target="_blank" rel="noopener noreferrer">{contractor.webSite}</a></p>
              ) : null}
              <p className="contractor-card-notes">{contractor.notes}</p>
              <div className="contractor-card-external">
                <strong>Other reviews:</strong>
                <ExternalRatingLinksCell contractor={contractor} />
              </div>
              <div className="contractor-card-actions">
                {user ? (
                  <button type="button" className="save-button" onClick={() => setRatingContractor(contractor)}>
                    {myRatings[contractor.id]?.stars ? `Your rating: ${myRatings[contractor.id].stars}★` : "Rate & review"}
                  </button>
                ) : null}
                {isAdmin ? (
                  <>
                    <button type="button" className="edit-button" onClick={() => setEditContractor(contractor)} disabled={isSubmitting}>Edit</button>
                    <button type="button" className="delete-button" onClick={() => handleDeleteContractor(contractor.id)} disabled={isSubmitting}>Delete</button>
                  </>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="table-container">
          <table className="contractor-table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Business</th>
                <th className="table-header-cell">Rating</th>
                <th className="table-header-cell">Contact</th>
                <th className="table-header-cell">Phone</th>
                <th className="table-header-cell">Email</th>
                <th className="table-header-cell">Notes</th>
                <th className="table-header-cell">Other reviews</th>
                <th className="table-header-cell">Reviews</th>
                {isAdmin ? <th className="table-header-cell">Admin</th> : null}
              </tr>
            </thead>
            <tbody>
              {displayedContractors.map((contractor) => (
                <tr key={contractor.id} className="table-row">
                  <td className="table-cell">
                    <strong>{contractor.businessName}</strong>
                    {contractor.webSite ? (
                      <div><a href={contractor.webSite} target="_blank" rel="noopener noreferrer" className="contractor-link">Website</a></div>
                    ) : null}
                  </td>
                  <td className="table-cell contractor-rating-cell">
                    <StarDisplay value={contractor.averageRating} />
                    <span className="contractor-rating-meta">
                      {formatRating(contractor.averageRating)} · {contractor.ratingCount || 0} review{(contractor.ratingCount || 0) === 1 ? "" : "s"}
                    </span>
                    <span className="contractor-rating-badge">{ratingLabel(contractor.averageRating)}</span>
                  </td>
                  <td className="table-cell">{contractor.contactName}</td>
                  <td className="table-cell">{contractor.phoneNumber}</td>
                  <td className="table-cell">{contractor.emailAddress}</td>
                  <td className="table-cell contractor-notes-cell">{contractor.notes}</td>
                  <td className="table-cell contractor-external-cell">
                    <ExternalRatingLinksCell contractor={contractor} />
                  </td>
                  <td className="table-cell">
                    {user ? (
                      <button type="button" className="save-button contractor-rate-btn" onClick={() => setRatingContractor(contractor)}>
                        {myRatings[contractor.id]?.stars ? `Your ${myRatings[contractor.id].stars}★` : "Rate"}
                      </button>
                    ) : (
                      <span className="contractor-review-muted">Sign in to rate</span>
                    )}
                  </td>
                  {isAdmin ? (
                    <td className="table-cell">
                      <div className="action-buttons">
                        <button type="button" onClick={() => setEditContractor(contractor)} className="edit-button" disabled={isSubmitting}>Edit</button>
                        <button type="button" onClick={() => handleDeleteContractor(contractor.id)} className="delete-button" disabled={isSubmitting}>Delete</button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ContractorsPage;